import { Table } from '@/components'
import { formatDealerAmount } from '@/utils/currency'
import { loadingTableHandler } from '@/utils/handler/loadingTableHandler'
import { ROUTESPATH } from '@/routes'
import { MoreOutlined } from '@ant-design/icons'
import { Dropdown, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from "dayjs"
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IoDocumentTextOutline, IoSwapHorizontalOutline, IoLinkOutline, IoPencilOutline } from 'react-icons/io5'
import { ORDER_STATUS_LABELS, OrderStatus, PAYMENT_STATUS_COLORS, PAYMENT_STATUS_LABELS } from '../../constants'
import { IDataverseOrder } from '../../types'
import { getOrderNumber, getOrderStatusColor } from '../../utils'
import { OrderDetailView } from '../OrderDetailView'

interface OrderTableProps {
    isLoading: boolean
    orders: IDataverseOrder[]
    current?: number
    pageSize?: number
    total?: number
    nextLink?: string
    totalRecordCountLimitExceeded?: boolean
    onPageChange?: (page: number, pageSize: number) => void
    onChangeOrderStatus?: (order: IDataverseOrder) => void
    onDownloadPDF?: (order: IDataverseOrder) => void
    onResendPaymentLink?: (order: IDataverseOrder) => void
    onEditEvolutionJobNumber?: (order: IDataverseOrder) => void
}

const canEditEvolutionJobNumber = (statuscode?: number) =>
    statuscode === OrderStatus.IN_PRODUCTION || statuscode === OrderStatus.Completed

export const OrderTable: React.FC<OrderTableProps> = ({
    isLoading,
    orders,
    current,
    pageSize,
    total,
    nextLink,
    totalRecordCountLimitExceeded,
    onPageChange,
    onChangeOrderStatus,
    onDownloadPDF,
    onResendPaymentLink,
    onEditEvolutionJobNumber,
}) => {
    const navigate = useNavigate();
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

    const toggleExpand = (record: IDataverseOrder) => {
        setExpandedRowKeys((prev) =>
            prev.includes(record.salesorderid)
                ? prev.filter((k) => k !== record.salesorderid)
                : [record.salesorderid]
        );
    };

    const columns: ColumnsType<IDataverseOrder> = [
        {
            title: 'Order Number',
            key: 'ordernumber',
            className: 'font-semibold',
            render: (_: unknown, record: IDataverseOrder) => (
                <span
                    className="text-primary-500 hover:text-primary-500 cursor-pointer hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(ROUTESPATH.ORDER_DETAIL.replace(':id', record.salesorderid));
                    }}
                >
                    {getOrderNumber(record)}
                </span>
            ),
        },
        {
            title: 'Customer/Company',
            dataIndex: '_customerid_value@OData.Community.Display.V1.FormattedValue',
            key: 'name',
            render: (value, order) => {
                return <div>
                    <p>{value || '-'}</p>
                    <p className='text-text-secondary'>{order?.quoteid?.["_dogma_customer_value@OData.Community.Display.V1.FormattedValue"] || '-'}</p>
                </div>
            }
        },
        {
            title: 'Quote Number',
            key: 'quotenumber',
            render: (_: unknown, record: IDataverseOrder) =>
                record.quoteid?.quotenumber ? (
                    <span
                        className="text-primary-500 hover:text-primary-500 cursor-pointer hover:underline"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(ROUTESPATH.QUOTE_DETAIL.replace(':id', record.quoteid!.quoteid));
                        }}
                    >
                        {record.quoteid.quotenumber}
                    </span>
                ) : '-',
        },
        {
            title: 'Project Reference',
            dataIndex: ["quoteid", "dogma_projectreference"],
            key: 'dogma_projectreference',
        },
        // {
        //     title: 'Project Type',
        //     key: 'dogma_projecttype',
        //     render: (_: unknown, record: IDataverseOrder) =>
        //         record.quoteid?.['dogma_projecttype@OData.Community.Display.V1.FormattedValue'] || '-',
        // },
        {
            title: 'Internal PO',
            dataIndex: 'dogma_internalpo',
            key: 'dogma_internalpo',
            render: (value: string | null | undefined) => value || '-',
        },
        {
            title: 'Evolution Job No',
            dataIndex: 'dogma_evolutionjobnumber',
            key: 'dogma_evolutionjobnumber',
            render: (value: string | null | undefined) => value || '-',
        },
        {
            title: 'Region',
            key: 'region',
            dataIndex: ["dogma_region@OData.Community.Display.V1.FormattedValue"]
        },
        {
            title: 'Date Ordered',
            dataIndex: 'createdon',
            key: 'createdon',
            render: (value) => {
                return value ? dayjs(value).format("DD-MM-YYYY") : '-'
            }
        },
        {
            title: 'Amount',
            dataIndex: 'totalamount',
            key: 'totalamount',
            className: "font-medium",
            align: "right",
            render: (_: number | undefined, record: IDataverseOrder) => {
                const amount = record.dogma_vsstotalamount ?? 0;
                const dealersCurrency = record.quoteid?.dogma_DealersCurrency;
                const exchangeRate = record.quoteid?.dogma_exchangerate ?? 1;
                return (
                    <div className="flex flex-col font-semibold items-end">
                        <span>{formatDealerAmount(amount, dealersCurrency, exchangeRate)}</span>
                    </div>
                );
            }
        },
        {
            title: 'Payment Status',
            dataIndex: "dogma_paymentstatus@OData.Community.Display.V1.FormattedValue",
            key: 'dogma_paymentstatus',
            render: (statusLabel: string | undefined, record: IDataverseOrder) => (
                <Tag
                    color={PAYMENT_STATUS_COLORS[record.dogma_paymentstatus] || 'default'}
                    style={{ borderRadius: '12px', padding: '4px 12px', border: 'none' }}
                >
                    {statusLabel || PAYMENT_STATUS_LABELS[record.dogma_paymentstatus] || 'Unknown'}
                </Tag>
            )
        },
        {
            title: 'Order Status',
            dataIndex: 'statuscode@OData.Community.Display.V1.FormattedValue',
            key: 'statuscode',
            render: (statusLabel: string | undefined, record: IDataverseOrder) => {
                const label = statusLabel || ORDER_STATUS_LABELS[record.statuscode] || 'Unknown';
                return (
                    <Tag
                        color={getOrderStatusColor(record.statuscode, label)}
                        style={{ borderRadius: '12px', padding: '4px 12px', border: 'none' }}
                    >
                        {label}
                    </Tag>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (_: unknown, record: IDataverseOrder) => {
                const menuItems = [
                    ...(record.paymenttermscode === 1 ? [{
                        key: 'resend-payment',
                        icon: <IoLinkOutline className="text-base" />,
                        label: 'Resend Payment Link',
                        onClick: () => onResendPaymentLink?.(record),
                    }] : []),
                    {
                        key: 'change-order',
                        icon: <IoSwapHorizontalOutline className="text-base" />,
                        label: 'Change Order Status',
                        onClick: () => onChangeOrderStatus?.(record),
                    },
                    {
                        key: 'download-pdf',
                        icon: <IoDocumentTextOutline className="text-base" />,
                        label: 'Download PDF',
                        onClick: () => onDownloadPDF?.(record),
                    },
                    ...(canEditEvolutionJobNumber(record.statuscode) ? [{
                        key: 'edit-evolution-job-number',
                        icon: <IoPencilOutline className="text-base" />,
                        label: 'Edit Evolution Job Number',
                        onClick: () => onEditEvolutionJobNumber?.(record),
                    }] : []),
                ];

                return (
                    <Dropdown
                        rootClassName="row_actions_dropdown"
                        menu={{ items: menuItems }}
                    >
                        <MoreOutlined />
                    </Dropdown>
                );
            },
        },
    ];

    return (
        loadingTableHandler(isLoading, <Table<IDataverseOrder>
            columns={columns}
            dataSource={orders}
            rowKey="salesorderid"
            isLoading={isLoading}
            current={current}
            pageSize={pageSize}
            total={total}
            nextLink={nextLink}
            totalRecordCountLimitExceeded={totalRecordCountLimitExceeded}
            handleChange={onPageChange}
            expandable={{
                expandedRowKeys,
                expandRowByClick: false,
                showExpandColumn: true,
                onExpand: (_expanded, record) => toggleExpand(record),
                expandedRowRender: (record) => (
                    <div style={{ width: 0, minWidth: '100%', overflow: 'hidden' }}>
                        <OrderDetailView
                            orderId={record.salesorderid}
                            onChangeOrderStatus={() => onChangeOrderStatus?.(record)}
                            onEditEvolutionJobNumber={() => onEditEvolutionJobNumber?.(record)}
                            isInline={true}
                        />
                    </div>
                ),
            }}
        />)
    )
}
